import { useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';
import { FormProvider, type SubmitHandler, useForm } from 'react-hook-form';

import { zodResolver } from '@hookform/resolvers/zod';

import { z } from 'zod';

import { Button } from '@/components/common/button/Button';
import { Flex } from '@/components/common/Flex';
import { FormSubmitButton } from '@/components/common/form/FormSubmitButton';
import { FormTextInput } from '@/components/common/form/FormTextInput';
import { Text } from '@/components/common/Text';

import styles from './TwoFaAuth.module.scss';

type TwoFaAuthProps = {
  onSubmit: CodeFormHandler;
  onCancel: () => unknown;
};

const useFormSchema = () => {
  const tErrors = useTranslations('errors');

  return useMemo(
    () =>
      z.object({
        code: z.string().min(1, tErrors('field is required')),
      }),
    [tErrors]
  );
};

export type CodeFormData = z.infer<ReturnType<typeof useFormSchema>>;

export type CodeFormHandler = SubmitHandler<CodeFormData>;

export const TwoFaAuth: FC<TwoFaAuthProps> = (props) => {
  const t = useTranslations('pages.account.login.form.2faAuthBlock');
  const tButtons = useTranslations('buttons');

  const { onSubmit, onCancel } = props;

  const form = useForm<CodeFormData>({
    resolver: zodResolver(useFormSchema()),
    mode: 'onBlur',
  });

  const { register, handleSubmit } = form;

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" gap={4}>
          <Flex className={styles.titleBlock} direction="column" gap={4}>
            <Text type="medium64" whiteSpace="preWrap">
              {t('title')}
            </Text>
          </Flex>

          <Text type="medium18">{t('subtitle')}</Text>

          <Flex className={styles.formContainer} direction="column">
            <FormTextInput {...register('code')} required placeholder={t('fields.code.placeholder')} />

            <Flex direction="column" gap={4}>
              <FormSubmitButton>{t('submitButton')}</FormSubmitButton>

              <Button variant="secondary" onClick={onCancel}>
                {tButtons('cancel')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </form>
    </FormProvider>
  );
};
